import { useEffect, useRef, useState } from 'react';
import ActionStatusText from '../../components/ui/ActionStatusText';
import ErrorMessage from '../../components/ui/ErrorMessage';
import { exerciseEditorUtils, normalizeTime } from '../../helpers';
import useUpdateFavoriteSessionItem from '../../hooks/data/exercise-sessions/favorite/useUpdateFavoriteSessionItem';
import useExerciseForm from '../../hooks/exercise/useExerciseForm';
import ExerciseTypeSearch from '../components/ExerciseTypeSearch';
import ModalHeader from '../components/ModalHeader';

const ModalFavoriteItemEditor = ({ exercise, onSave, onClose, onGoBack }) => {
    const isCustom = Number(exercise?.type) === 3;

    const { normalizeStandard, normalizeCustom, buildFavoriteItemPayload } = exerciseEditorUtils;
    const initialRef = useRef(null);

    const {
        form,
        handleChange,
        handleSelect,
        clearSearch,
        filteredResults,
        dropdownRef,
        showSuggestions,
        showInvalidMessage,
        resetForm,
        isLoading: isLoadingTypes,
        isError: isErrorTypes
    } = useExerciseForm();

    const { updateItem, isUpdating, isSuccess } = useUpdateFavoriteSessionItem();

    const [customData, setCustomData] = useState({
        name: '',
        duration: '',
        kcal: '',
        time: '',
        comment: ''
    });

    // INIT
    useEffect(() => {
        if (!exercise) return;

        if (isCustom) {
            const initial = {
                name: exercise.name,
                duration: exercise.duration,
                kcal: exercise.kcal,
                time: normalizeTime(exercise.time),
                comment: exercise.comment
            };

            setCustomData(initial);
            initialRef.current = initial;
        } else {
            const initial = {
                exercise_id: exercise.exercise_id,
                exercise_name: exercise.name,
                exercise_duration: exercise.duration,
                exercise_time: normalizeTime(exercise.time),
                exercise_comment: exercise.comment
            };

            resetForm(initial);
            initialRef.current = initial;
        }
    }, [exercise, isCustom, resetForm]);

    const isLocked = isLoadingTypes || isUpdating || isSuccess;

    // CHANGE DETECTION
    const hasChanged = (() => {
        if (!initialRef.current) return false;

        return isCustom
            ? JSON.stringify(normalizeCustom(customData)) !==
                  JSON.stringify(normalizeCustom(initialRef.current))
            : JSON.stringify(normalizeStandard(form)) !==
                  JSON.stringify(normalizeStandard(initialRef.current));
    })();

    // VALIDATION
    const isFormIncomplete = isCustom
        ? !customData.name || !customData.duration || !customData.kcal || !customData.time
        : !form.exercise_id || !form.exercise_duration || !form.exercise_time;

    // SUBMIT
    const handleSubmit = async () => {
        try {
            const payload = buildFavoriteItemPayload({
                isCustom,
                form,
                customData,
                normalizeTime
            });

            const updated = await updateItem({
                itemId: exercise.id,
                data: payload
            });

            onSave(updated);
        } catch (err) {
            console.error('Failed to update favorite exercise', err);
        }
    };

    return (
        <>
            <ModalHeader
                title="Redigera favoritmotion"
                onClose={onClose}
                onGoBack={onGoBack}
                disableButtons={isUpdating}
            />

            <div className="modal-body exercise">
                {isErrorTypes ? (
                    <ErrorMessage message="Kunde inte ladda övningar." />
                ) : (
                    <div className="exercise-input-wrapper" ref={dropdownRef}>
                        {!isCustom && (
                            <ExerciseTypeSearch
                                form={form}
                                filteredResults={filteredResults}
                                showSuggestions={showSuggestions}
                                showInvalidMessage={showInvalidMessage}
                                handleChange={handleChange}
                                handleSelect={handleSelect}
                                clearSearch={clearSearch}
                                isLocked={isLocked}
                                mode="edit"
                            />
                        )}

                        {isCustom && (
                            <div className="input-with-label">
                                <span className="inline-label">Namn:</span>
                                <input
                                    type="text"
                                    className="modal-input name-input"
                                    value={customData.name}
                                    onChange={(e) => setCustomData((p) => ({ ...p, name: e.target.value }))}
                                    disabled={isLocked}
                                />
                            </div>
                        )}

                        <div className="input-with-label">
                            <span className="inline-label">Tid (min):</span>
                            <input
                                type="number"
                                className="modal-input duration-input"
                                min="1"
                                value={isCustom ? customData.duration : form.exercise_duration}
                                onChange={(e) =>
                                    isCustom
                                        ? setCustomData((p) => ({ ...p, duration: e.target.value }))
                                        : handleChange(e)
                                }
                                name={isCustom ? undefined : 'exercise_duration'}
                                disabled={isLocked}
                            />
                        </div>

                        {isCustom && (
                            <div className="input-with-label">
                                <span className="inline-label">Kcal:</span>
                                <input
                                    type="number"
                                    className="modal-input kcal-input"
                                    min="0"
                                    value={customData.kcal}
                                    onChange={(e) => setCustomData((p) => ({ ...p, kcal: e.target.value }))}
                                    disabled={isLocked}
                                />
                            </div>
                        )}

                        <div className="input-with-label">
                            <span className="inline-label">Klockslag:</span>
                            <input
                                type="time"
                                className="modal-input time-input"
                                value={isCustom ? customData.time : form.exercise_time}
                                onChange={(e) =>
                                    isCustom
                                        ? setCustomData((p) => ({ ...p, time: e.target.value }))
                                        : handleChange(e)
                                }
                                name={isCustom ? undefined : 'exercise_time'}
                                disabled={isLocked}
                            />
                        </div>

                        <div className="input-with-label">
                            <span className="inline-label">Kommentar:</span>
                            <input
                                type="text"
                                className="modal-input comment-input"
                                value={isCustom ? customData.comment : form.exercise_comment}
                                onChange={(e) =>
                                    isCustom
                                        ? setCustomData((p) => ({ ...p, comment: e.target.value }))
                                        : handleChange(e)
                                }
                                name={isCustom ? undefined : 'exercise_comment'}
                                disabled={isLocked}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className="modal-footer exercise">
                <button
                    type="button"
                    className="btn-green bold-title"
                    disabled={isLocked || isFormIncomplete || !hasChanged}
                    onClick={handleSubmit}
                >
                    <ActionStatusText
                        defaultLabel="Spara ändringar"
                        successLabel="Ändringar har sparats!"
                        isProcessing={isUpdating}
                        isCompleted={isSuccess}
                    />
                </button>

                <button
                    type="button"
                    className="marked-border-btn bold-title"
                    disabled={isUpdating}
                    onClick={onGoBack}
                >
                    Gå tillbaka
                </button>
            </div>
        </>
    );
};

export default ModalFavoriteItemEditor;
